import React, {ReactElement, useEffect, useState} from 'react';
import {ModalContainer} from "../ModalContainer";
import {ModalSize} from "../ModalContainer/ModalContainer";
import {Dialog, Tab} from "@headlessui/react";
import {TabButtonUnderlined} from "../TabButtonUnderlined";
import {ExhibitsPanel} from "./Panels/ExhibitsPanel";
import {FacilitiesPanel} from "./Panels/FacilitiesPanel";
import {EventsPanel} from "./Panels/EventsPanel";
import {PlacesPanel} from "./Panels/PlacesPanel";
import FloatingDropdown from "../FloatingDropdown";
import {useQuery} from "react-query";
import ngage from "../../services/ngage";
import {Site} from "../../types/Site";
import {useOperator} from "../../context/OperatorContext";
import {CollectionsPanel} from "./Panels/CollectionsPanel";
import {defaultSelectedItems, ENTITY_TYPES} from "../EntitySelector/EntitySelector.constants";
import {
  type EntityType,
  OptionalSelectedItemsData, SelectedItem,
  type SelectedItemsData
} from "components/EntitySelector/EntitySelector.types";
import {toTitleCase} from "../../services/string";
import {ZonesPanel} from "./Panels/ZonesPanel";
import {AchievementsPanel} from "./Panels/AchievementsPanel";
import {ArticlesPanel} from "./Panels/ArticlesPanel";
import {QuizzesPanel} from "./Panels/QuizzesPanel";
import {SitesPanel} from "./Panels/SitesPanel";
import {TrailsPanel} from "./Panels/TrailsPanel";
import {SelectedItemsWrapper} from "./SelectedItemsWrapper";

// Constants ----

const defaultEntityTypes: EntityType[] = [
  ENTITY_TYPES.ACHIEVEMENT,
  ENTITY_TYPES.ARTICLE,
  ENTITY_TYPES.COLLECTION,
  ENTITY_TYPES.EVENT,
  ENTITY_TYPES.EXHIBIT,
  ENTITY_TYPES.FACILITY,
  ENTITY_TYPES.PLACE,
  ENTITY_TYPES.QUIZ,
  ENTITY_TYPES.SITE,
  ENTITY_TYPES.TRAIL,
  ENTITY_TYPES.ZONE,
];

// Types ----

type SiteItem = {
  label: string
  value: string | null
}

type SelectorModalProps = {
  title: string
  show: boolean
  onConfirm: (items: SelectedItemsData) => void
  onCancel: () => void
  entityTypes?: EntityType[]
  siteId?: number | null
  maxSelectedItems?: number
  initialSelectedItems?: OptionalSelectedItemsData
}

// Component ----

function EntitySelectorModal({ title, show, onConfirm, onCancel, entityTypes = defaultEntityTypes, siteId, maxSelectedItems, initialSelectedItems = {} }: SelectorModalProps) {
  const {termFor, isMultiSite} = useOperator();
  const [panelMode, setPanelMode] = useState<EntityType>(getTabIndex(0));
  const [selectedItems, setSelectedItems] = useState<SelectedItemsData>({...defaultSelectedItems, ...initialSelectedItems});
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(siteId ?? null);

  const selectedItemCount: number = Object.values(selectedItems).reduce((total, items) => total + items.length, 0);
  const isMaxSelected = maxSelectedItems ? selectedItemCount >= maxSelectedItems : false;

  const {data: sites, isLoading: sitesLoading} = useQuery(['sites.filtered'], () => ngage.sites.filtered(), {enabled: show});
  const hasSiteFilter = isMultiSite && !siteId && (
    panelMode === ENTITY_TYPES.ARTICLE ||
    panelMode === ENTITY_TYPES.COLLECTION ||
    panelMode === ENTITY_TYPES.EVENT ||
    panelMode === ENTITY_TYPES.EXHIBIT ||
    panelMode === ENTITY_TYPES.FACILITY ||
    panelMode === ENTITY_TYPES.QUIZ ||
    panelMode === ENTITY_TYPES.ZONE
  );

  // Set the selected site id if the siteId prop changes
  useEffect(() => {
    if (!siteId) return;
    setSelectedSiteId(siteId);
  }, [siteId])

  // Set the selected items when the modal is shown
  useEffect(() => {
    if (!show) return;
    setSelectedItems({...defaultSelectedItems, ...initialSelectedItems});
  }, [initialSelectedItems, show]);

  // Reset the panel mode if the entity types change
  useEffect(() => {
    setPanelMode(getTabIndex(0));
  }, [entityTypes, getTabIndex]);

  function handleSelectItem<K extends EntityType>(item: SelectedItem<K>, entityType: K) {
    setSelectedItems(current => {
      // Check if the item is already selected
      const alreadySelected = current[entityType].some(selectedItem => selectedItem.id === item.id);

      // If the item is already selected, remove it.
      if (alreadySelected) {
        const newSelectedItems = [...current[entityType]].filter(selectedItem => selectedItem.id !== item.id);

        return {
          ...current,
          [entityType]: newSelectedItems
        }
      }

      // Check is the maximum is already selected
      if (isMaxSelected) return current;

      // Otherwise, add the item to the selected items.
      return {
        ...current,
        [entityType]: [...current[entityType], item]
      }
    })
  }

  function handleRemoveItem<K extends EntityType>(item: SelectedItem<K>, entityType: K) {
    setSelectedItems(current => {
      const newSelectedItems = [...current[entityType]].filter(selectedItem => selectedItem.id !== item.id);

      return {
        ...current,
        [entityType]: newSelectedItems
      }
    })
  }

  function handleClearItems() {
    setSelectedItems(defaultSelectedItems);
  }

  function handleCancel() {
    handleClearItems();
    onCancel();
    if (!siteId) setSelectedSiteId(null);
  }

  async function handleSelectedItems() {
    onConfirm(selectedItems);
    handleClearItems();
  }

  function getMaxSelectedItemsText() {
    if (maxSelectedItems === 1) return 'You can only select 1 item';
    return `You can select a maximum of ${maxSelectedItems} items`;
  }

  // Create site items for dropdown
  function getSiteItems(sites: Site[] = []): SiteItem[] {
    const allSites: SiteItem = {label: `All ${termFor('sites')}`, value: null};
    const dynamicTypes = sites.map(site => ({label: site.name, value: site.id.toString()}));

    return [allSites, ...dynamicTypes];
  }

  // Get site items for dropdown
  const siteItems = getSiteItems(sites?.data);

  // Get the panels to be displayed
  function getPanels() {
    const panels: ReactElement[] = [];

    entityTypes.forEach((entityType) => {
      switch (entityType) {
        case "achievement":
          panels.push(
            <AchievementsPanel
              onSelect={achievement => handleSelectItem(achievement, ENTITY_TYPES.ACHIEVEMENT)}
              selectedItems={selectedItems}
              isMaxSelected={isMaxSelected}
              key={entityType}
            />
          )
          break;
        case "article":
          panels.push(
            <ArticlesPanel
              onSelect={article => handleSelectItem(article, ENTITY_TYPES.ARTICLE)}
              selectedItems={selectedItems}
              siteId={selectedSiteId}
              isMaxSelected={isMaxSelected}
              key={entityType}
            />
          )
          break;
        case "collection":
          panels.push(
            <CollectionsPanel
              onSelect={collection => handleSelectItem(collection, ENTITY_TYPES.COLLECTION)}
              selectedItems={selectedItems}
              siteId={selectedSiteId}
              isMaxSelected={isMaxSelected}
              key={entityType}
            />
          )
          break;
        case "event":
          panels.push(
            <EventsPanel
              onSelect={event => handleSelectItem(event, ENTITY_TYPES.EVENT)}
              selectedItems={selectedItems}
              siteId={selectedSiteId}
              isMaxSelected={isMaxSelected}
              isMultiSite={isMultiSite}
              key={entityType}
            />
          )
          break;
        case "exhibit":
          panels.push(
            <ExhibitsPanel
              onSelect={exhibit => handleSelectItem(exhibit, ENTITY_TYPES.EXHIBIT)}
              selectedItems={selectedItems}
              siteId={selectedSiteId}
              isMaxSelected={isMaxSelected}
              isMultiSite={isMultiSite}
              key={entityType}
            />
          )
          break;
        case "facility":
          panels.push(
            <FacilitiesPanel
              onSelect={facility => handleSelectItem(facility, ENTITY_TYPES.FACILITY)}
              selectedItems={selectedItems}
              siteId={selectedSiteId}
              isMaxSelected={isMaxSelected}
              isMultiSite={isMultiSite}
              key={entityType}
            />
          )
          break;
        case "place":
          panels.push(
            <PlacesPanel
              onSelect={place => handleSelectItem(place, ENTITY_TYPES.PLACE)}
              selectedItems={selectedItems}
              isMaxSelected={isMaxSelected}
              key={entityType}
            />
          )
          break;
        case "quiz":
          panels.push(
            <QuizzesPanel
              onSelect={quiz => handleSelectItem(quiz, ENTITY_TYPES.QUIZ)}
              selectedItems={selectedItems}
              siteId={selectedSiteId}
              isMaxSelected={isMaxSelected}
              key={entityType}
            />
          )
          break;
        case "site":
          panels.push(
            <SitesPanel
              onSelect={site => handleSelectItem(site, ENTITY_TYPES.SITE)}
              selectedItems={selectedItems}
              isMaxSelected={isMaxSelected}
              key={entityType}
            />
          )
          break;
        case "trail":
          panels.push(
            <TrailsPanel
              onSelect={trail => handleSelectItem(trail, ENTITY_TYPES.TRAIL)}
              selectedItems={selectedItems}
              isMaxSelected={isMaxSelected}
              key={entityType}
            />
          )
          break;
        case "zone":
          panels.push(
            <ZonesPanel
              onSelect={zone => handleSelectItem(zone, ENTITY_TYPES.ZONE)}
              selectedItems={selectedItems}
              siteId={selectedSiteId}
              isMaxSelected={isMaxSelected}
              isMultiSite={isMultiSite}
              key={entityType}
            />
          )
          break;
      }
    })

    return panels;
  }

  // Get the tabs needed to be displayed
  function getTabs() {
    const tabs: ReactElement[] = [];

    entityTypes.forEach((entityType) => {
      tabs.push(<TabButtonUnderlined key={entityType}>{termFor(toTitleCase(entityType))}</TabButtonUnderlined>)
    })

    return tabs;
  }

  // Get the index of the entity type
  function getTabIndex(index: number) {
    return entityTypes?.[index];
  }

  return (
    <ModalContainer
      show={show}
      onApprove={handleSelectedItems}
      onCancel={handleCancel}
      approveLabel={`Choose ${selectedItemCount} items`}
      approveDisabled={!selectedItemCount}
      size={ModalSize.X_LARGE}
    >
      <div className="flex flex-col gap-5">
        <div className='flex flex-col gap-1 border-b border-grey-200 pb-4'>
          <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">{title}</Dialog.Title>
          {!!maxSelectedItems && <p className='text-sm text-gray-500'>{getMaxSelectedItemsText()}</p>}
        </div>

        <Tab.Group onChange={(index) => {
          const entity = getTabIndex(index);
          setPanelMode(entity);
        }}>
          <div className='flex gap-6 flex-wrap items-center'>
            {/* Buttons */}
            <Tab.List className="flex flex-wrap gap-x-7 gap-y-1">
              {getTabs()}
            </Tab.List>

            <div className={`ml-auto ${!hasSiteFilter && 'invisible'}`}>
              <FloatingDropdown
                items={siteItems}
                selectedIndex={Math.max(siteItems.findIndex((item) => item.value === selectedSiteId?.toString()), 0)}
                onSelect={(item) => setSelectedSiteId(item.value)}
                loading={sitesLoading}
              />
            </div>
          </div>

          {/* Panels */}
          <div className='grid gap-6 grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] mt-1'>
            <Tab.Panels>
              {getPanels()}
            </Tab.Panels>

            {/* Selected items */}
            <SelectedItemsWrapper
              selectedItems={selectedItems}
              selectedItemCount={selectedItemCount}
              onRemoveItem={handleRemoveItem}
              onClearItems={handleClearItems}
              isMultiSite={isMultiSite}
            />
          </div>
        </Tab.Group>
      </div>
    </ModalContainer>
  );
}

export {EntitySelectorModal};